import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { auth } from '../utils/api';
import { toast } from 'sonner';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await auth.requestPasswordReset(email);
            setSubmitted(true);
        } catch (err) {
            if (err.response?.status === 429) {
                toast.error('Too many attempts. Please wait a minute before trying again.');
            } else {
                toast.error(err.userMessage || 'Something went wrong. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F0FDF4' }}>
                <div className="bg-white rounded-3xl shadow-sm border border-purple-200/50 max-w-md w-full p-8 text-center">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#F3E8FF' }}>
                        <Mail className="h-8 w-8" style={{ color: '#9333EA' }} />
                    </div>
                    <h1 className="text-2xl font-bold mb-2 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="reset-email-sent-heading">Check Your Email</h1>
                    <p className="text-slate-500 mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
                        If an account exists for <strong className="text-[#0A0A0A]">{email}</strong>, we've sent a password reset link. Check your inbox.
                    </p>
                    <Link to="/login">
                        <Button variant="outline" className="w-full rounded-full border-purple-200 hover:border-purple-400 hover:bg-purple-50" data-testid="back-to-login-btn">
                            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Login
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F0FDF4' }}>
            <div className="bg-white rounded-3xl shadow-sm border border-purple-200/50 max-w-md w-full p-8">
                <div className="flex items-center space-x-2 mb-8 justify-center">
                    <img src="/sampler-logo.png" alt="Sampler" className="h-9 w-auto" />
                    <span className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</span>
                </div>
                <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-2 text-center" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    Account Recovery
                </span>
                <h1 className="text-2xl font-bold mb-2 text-center tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="forgot-password-heading">
                    Forgot Password
                </h1>
                <p className="text-slate-500 mb-6 text-center text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
                    Enter your email and we'll send you a reset link.
                </p>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <Input
                            type="email"
                            placeholder="your@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="rounded-xl border-purple-200/50"
                            data-testid="forgot-password-email-input"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={loading || !email}
                        className="w-full btn-primary h-12"
                        data-testid="send-reset-link-btn"
                    >
                        {loading ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                </form>
                <div className="mt-4 text-center">
                    <Link to="/login" className="text-sm text-[#8B5CF6] hover:underline font-semibold">
                        Back to Login
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
